import React, {
  memo,
  useCallback,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import "../../css/FullEvent/FullEventInputForm.css";
import "../../css/GlobalStyles.css";
import {
  AccountData,
  CustomTicketV2,
  Event,
  PromoCode,
  PromoCodeStatus,
  TicketType,
} from "@markit/common.types";
import { FormatCurrency } from "../../utils/FormatCurrency";
import { ErrorCode } from "@markit/common.api";
import { MixpanelContext } from "../../context/AnalyticsService";
import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import { useTheme } from "../../hooks/useTheme";
import CustomTextField from "../../components/CustomTextField";
import RectangleButton from "../Buttons/RectangleButton";
import CheckoutTotalSummary from "../CheckoutTotalSummary";
import { findFreeTicketAlias } from "../../utils/eventUtils/eventUtils";
import {
  getEffectivePriceCents,
  hasInvalidFlexiblePriceInput,
  isTotalBelowStripeMinimum,
} from "../../utils/eventUtils/customTicketUtils";
import { useOnMount } from "../../hooks/useOnMount";
import FullEventPriceDisplay from "./FullEventPriceDisplay";
import FullEventTipInput from "./FullEventTipInput";
import {
  GetDateWithTime,
  isPaidTicketType,
  isProductSpecificType,
} from "@markit/common.utils";
import EventItemImage from "../DisplayItem/EventItem/EventItemImage";
import { getUrlParamState } from "../../redux/slices/urlParamSlice";
import { useSelector } from "react-redux";

type FullEventPaymentSummaryProps = {
  event: Event;
  host: AccountData;
  ticketForRedemption: CustomTicketV2 | undefined;
  setTicketForRedemption: (
    ticketForRedemption: CustomTicketV2 | undefined
  ) => void;
  promoCode: PromoCode | undefined;
  tixId: string;
  selectedCustomTickets: CustomTicketV2[];
  setSelectedCustomTickets: (selectedCustomTickets: CustomTicketV2[]) => void;
  ticketQuantities: Map<string, number>;
  setTicketQuantities: (ticketQuantities: Map<string, number>) => void;
  namedPrices: Map<string, number>;
  baseTicketPriceCents: number;
  ticketFeesCents: number;
  totalAmountCents: number;
  discountedTicketPriceCents: number;
  numTicketsUserInGroups: number[];
  disabled: boolean;
  updatePromoCode: (promoCode: PromoCode | undefined) => void;
  updateTixId: (tixId: string) => void;
  setIsRedeemFreePromoCode: (isRedeemFreePromoCode: boolean) => void;
  showTipSection: boolean;
  tipAmountCents: number;
  setTipAmountCents: (tipAmountCents: number) => void;
};

export const FullEventPaymentSummary = memo(function FullEventPaymentSummaryFn(
  props: FullEventPaymentSummaryProps
) {
  const {
    event,
    host,
    ticketForRedemption,
    setTicketForRedemption,
    promoCode,
    tixId,
    selectedCustomTickets,
    setSelectedCustomTickets,
    ticketQuantities,
    setTicketQuantities,
    namedPrices,
    baseTicketPriceCents,
    ticketFeesCents,
    totalAmountCents,
    discountedTicketPriceCents,
    numTicketsUserInGroups,
    disabled,
    updatePromoCode,
    updateTixId,
    setIsRedeemFreePromoCode,
    showTipSection,
    tipAmountCents,
    setTipAmountCents,
  } = props;
  const mixpanel = useContext(MixpanelContext);
  const { theme } = useTheme();
  const { promoCodeId: promoCodeUrlId } = useSelector(getUrlParamState);

  const [showPromoCodeInput, setShowPromoCodeInput] = useState<boolean>(false);
  const [promoCodeName, setPromoCodeName] = useState("");
  const [promoCodeValid, setPromoCodeValid] = useState<ErrorCode>(
    ErrorCode.NEUTRAL_PROMO
  );
  const [promoCodeError, setPromoCodeError] = useState<string>("");

  const isProductSpecific = useMemo(
    () => isProductSpecificType(event.eventType),
    [event.eventType]
  );

  const priceBelowMinimum = useMemo(
    () => isTotalBelowStripeMinimum(discountedTicketPriceCents),
    [discountedTicketPriceCents]
  );

  // Displays the base price of the ticket that is being redeemed via promo code, accounting for namedPrices (flexible prices)
  const effectivePromoBasePriceCents = useMemo(
    () =>
      ticketForRedemption
        ? getEffectivePriceCents(ticketForRedemption, namedPrices)
        : baseTicketPriceCents,
    [baseTicketPriceCents, namedPrices, ticketForRedemption]
  );

  useOnMount(() => {
    // Autofill the promo code input if there is a promo code in the url
    if (promoCode) {
      setShowPromoCodeInput(true);
      setPromoCodeName(promoCode.alias);
      setPromoCodeValid(ErrorCode.VALID_PROMO);
    }
  });

  // Whenever the selected custom ticket changes and there are no paid tickets selected, reset the promo code and input if it's currently shown
  // Only execute if there is no promo code in the url (tixId or promoCodeId)
  useEffect(() => {
    if (
      showPromoCodeInput &&
      !tixId &&
      !promoCodeUrlId &&
      !selectedCustomTickets.some((ticket) => isPaidTicketType(ticket))
    ) {
      setPromoCodeName("");
      setPromoCodeValid(ErrorCode.NEUTRAL_PROMO);
      setShowPromoCodeInput(false);
      updatePromoCode(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomTickets]);

  const hasPaidSelectedTicket = useMemo(
    () =>
      selectedCustomTickets.some((ticket) => ticket.type === TicketType.PAID) &&
      discountedTicketPriceCents > 0,
    [selectedCustomTickets, discountedTicketPriceCents]
  );

  const showPaymentSection = useMemo(
    () => hasPaidSelectedTicket && !disabled && !tixId,
    [disabled, tixId, hasPaidSelectedTicket]
  );

  const hasInvalidPriceInput = useMemo(
    () => hasInvalidFlexiblePriceInput(selectedCustomTickets, namedPrices),
    [selectedCustomTickets, namedPrices]
  );

  const checkPromoCode = useCallback(
    async (promoCodeName) => {
      const foundPromoCode = event.promoCodes.find(
        (code) => code.alias === promoCodeName.toUpperCase()
      );
      if (foundPromoCode) {
        if (foundPromoCode.status === PromoCodeStatus.INACTIVE) {
          setPromoCodeValid(ErrorCode.INVALID_PROMO);
          setPromoCodeError("Promo code no longer valid");
        } else if (
          foundPromoCode.maxUseCount !== undefined
            ? foundPromoCode.usedCount < foundPromoCode.maxUseCount
            : foundPromoCode.redeemedBy === "" ||
              foundPromoCode.redeemedBy === "unlimited" ||
              foundPromoCode.redeemedBy === "pending"
        ) {
          updatePromoCode(foundPromoCode);
          setPromoCodeValid(ErrorCode.VALID_PROMO);
          mixpanel.track("Webapp: Applied Promo Code Successfully", {
            event_id: event.id,
            promo_code: foundPromoCode.id,
          });
        } else if (foundPromoCode.usedCount >= foundPromoCode.maxUseCount) {
          setPromoCodeValid(ErrorCode.REACHED_MAX_USE);
          setPromoCodeError("Maximum use limit reached");
        } else {
          setPromoCodeValid(ErrorCode.ALREADY_USED);
          setPromoCodeError("Promo code already used");
        }
      } else {
        const foundFreeTicket = await findFreeTicketAlias(
          event.id,
          promoCodeName.toUpperCase()
        );
        if (foundFreeTicket[0]) {
          const foundCustomTicket = event.customTickets.find(
            (cTix) => cTix.id === foundFreeTicket[0]?.customTicketId
          );
          const foundCustomTicketIndex = event.customTickets.findIndex(
            (cTix) => cTix.id === foundFreeTicket[0]?.customTicketId
          );
          const reachedMaxSelectedTickets =
            foundCustomTicket &&
            numTicketsUserInGroups[foundCustomTicketIndex] >=
              foundCustomTicket.maxQuantity;
          if (foundFreeTicket[0].uid !== "") {
            setPromoCodeValid(ErrorCode.ALREADY_USED);
            setPromoCodeError("Promo code already used");
          } else if (reachedMaxSelectedTickets) {
            setPromoCodeValid(ErrorCode.ALREADY_USED);
            setPromoCodeError("Maximum quantity reached");
          } else {
            // Successful found free ticket to redeem
            updateTixId(foundFreeTicket[0].id);
            setIsRedeemFreePromoCode(true);
            setPromoCodeValid(ErrorCode.VALID_PROMO);

            if (foundCustomTicket) {
              setTicketForRedemption(foundCustomTicket);
              // Ensure the ticket this code is valid for is in the selected tickets array
              // Set the selected tickets to only this free ticket
              setSelectedCustomTickets([foundCustomTicket]);
              setTicketQuantities(
                new Map(ticketQuantities).set(foundCustomTicket.id, 1)
              );
            }
          }
        } else {
          setPromoCodeValid(foundFreeTicket[1]);
        }
      }
    },
    [
      event.promoCodes,
      event.id,
      event.customTickets,
      updatePromoCode,
      mixpanel,
      setIsRedeemFreePromoCode,
      numTicketsUserInGroups,
      updateTixId,
      setTicketQuantities,
      ticketQuantities,
      setTicketForRedemption,
      setSelectedCustomTickets,
    ]
  );

  const renderTicketQuantity = useCallback(
    (ticket: CustomTicketV2) => {
      const quantity = ticketQuantities.get(ticket.id) ?? 0;
      return (
        <div
          className="Centering sectionTitle"
          style={{
            borderRadius: 12,
            border: `1px solid ${theme.TextFieldBorder.borderColor}`,
            backgroundColor: theme.SecondaryBG.backgroundColor,
            width: 49,
            paddingBlock: 5,
            color: theme.PrimaryText.color,
          }}
        >
          {quantity}
        </div>
      );
    },
    [
      theme.PrimaryText.color,
      theme.SecondaryBG.backgroundColor,
      theme.TextFieldBorder.borderColor,
      ticketQuantities,
    ]
  );

  const renderPriceDisplay = useCallback(
    (customTicket: CustomTicketV2) => {
      const quantity = ticketQuantities.get(customTicket.id) ?? 0;
      return (
        <FullEventPriceDisplay
          eventType={event.eventType}
          ticketQuantity={quantity}
          customTicket={customTicket}
          selectedCustomTicket={customTicket}
          promoCode={promoCode}
          isTixId={tixId !== ""}
          isSoldOut={false}
          theme={theme}
          namedPrice={namedPrices.get(customTicket.id) ?? undefined}
        />
      );
    },
    [event.eventType, namedPrices, promoCode, theme, ticketQuantities, tixId]
  );

  return (
    <>
      <div className="AlignedRow" style={{ gap: 14 }}>
        <EventItemImage item={event} size={65} scaleImage />
        <div className="ColumnNormal" style={{ gap: 4 }}>
          <span className="bodyMedium TwoLineText" style={theme.PrimaryText}>
            {event.title}
          </span>
          <span className="bodySubtext">
            {isProductSpecific
              ? host.fullName
              : GetDateWithTime(event.start, true)}
          </span>
        </div>
      </div>
      <div className="ColumnNormal" style={{ gap: 14 }}>
        {selectedCustomTickets.map((customTicket) => (
          <div
            className="AlignedRowSpaced"
            style={{ gap: 14 }}
            key={customTicket.id}
          >
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span className="sectionTitle" style={theme.PrimaryText}>
                {customTicket.label}
              </span>
              {renderPriceDisplay(customTicket)}
            </div>
            {renderTicketQuantity(customTicket)}
          </div>
        ))}
      </div>
      {showPaymentSection || promoCodeValid === ErrorCode.VALID_PROMO ? (
        <>
          {!hasInvalidPriceInput ? (
            !showPromoCodeInput && !promoCode ? (
              <RectangleButton
                buttonLabel="Redeem a Code"
                onPress={() => setShowPromoCodeInput(!showPromoCodeInput)}
                altColor={theme.NewPrimaryBG.backgroundColor}
                altTextColor={theme.PrimaryText.color}
                containerStyles={{
                  padding: 14,
                  borderColor: theme.EditableTextFieldBorder.borderColor,
                  borderWidth: 1,
                }}
              />
            ) : (
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <div className="AlignedRowSpaced">
                  <CustomTextField
                    value={promoCodeName}
                    inputMode="text"
                    placeholder="Enter Code"
                    borderRadius={12}
                    onChange={(name: any) => {
                      setPromoCodeName(name.target.value);
                      setPromoCodeError("");
                      setPromoCodeValid(ErrorCode.NEUTRAL_PROMO);
                    }}
                    disabled={!!promoCode}
                    error={
                      promoCodeValid === ErrorCode.INVALID_PROMO ||
                      promoCodeValid === ErrorCode.ALREADY_USED ||
                      promoCodeValid === ErrorCode.REACHED_MAX_USE
                    }
                    endAdornment={
                      promoCodeValid === ErrorCode.VALID_PROMO ? (
                        <Icon
                          icon="ion:checkmark-circle"
                          height={20}
                          style={{
                            paddingTop: 1,
                            marginRight: 10,
                            color: Colors.GREEN2,
                          }}
                        />
                      ) : promoCodeValid === ErrorCode.INVALID_PROMO ||
                        promoCodeValid === ErrorCode.ALREADY_USED ||
                        promoCodeValid === ErrorCode.REACHED_MAX_USE ? (
                        <Icon
                          icon="ion:close-circle-outline"
                          height={20}
                          style={{
                            paddingTop: 1,
                            marginRight: 10,
                            color: Colors.RED3,
                          }}
                        />
                      ) : undefined
                    }
                    width="50%"
                  />
                  {promoCodeValid === ErrorCode.VALID_PROMO ||
                  ticketForRedemption ? (
                    <span style={theme.PrimaryText}>
                      -
                      {FormatCurrency(
                        (effectivePromoBasePriceCents -
                          discountedTicketPriceCents) /
                          100
                      )}
                    </span>
                  ) : (
                    <div>
                      <RectangleButton
                        buttonLabel={
                          <span className="bodyMedium">Apply Promo</span>
                        }
                        onPress={() => checkPromoCode(promoCodeName)}
                        altColor={theme.ButtonBG.backgroundColor}
                        altTextColor={theme.ButtonText.color}
                        altPaddingHorz={14}
                        disabled={!promoCodeName}
                      />
                    </div>
                  )}
                </div>
                {promoCodeError ? (
                  <span style={{ fontSize: 12, color: Colors.RED3 }}>
                    {promoCodeError}
                  </span>
                ) : undefined}
              </div>
            )
          ) : null}
          {showTipSection ? (
            <FullEventTipInput
              ticketSubtotalCents={discountedTicketPriceCents}
              tipAmountCents={tipAmountCents}
              setTipAmountCents={setTipAmountCents}
              hardTheme={event.theme}
            />
          ) : null}
          {hasPaidSelectedTicket ? (
            <CheckoutTotalSummary
              totalValue={
                hasInvalidPriceInput
                  ? "--"
                  : FormatCurrency(totalAmountCents / 100)
              }
              totalValueStyle={{
                color: priceBelowMinimum
                  ? Colors.RED3
                  : theme.PrimaryText.color,
              }}
              onTotalValuePress={
                priceBelowMinimum
                  ? () => alert("The minimum base price must be at least $1.")
                  : undefined
              }
              expandable={
                !disabled && !isProductSpecific && !hasInvalidPriceInput
              }
              lineItems={[
                {
                  label: "Ticket Price",
                  value: FormatCurrency(baseTicketPriceCents / 100),
                },
                ...(baseTicketPriceCents !== discountedTicketPriceCents
                  ? [
                      {
                        label: "Discount Applied",
                        value: `-${FormatCurrency(
                          (baseTicketPriceCents - discountedTicketPriceCents) /
                            100
                        )}`,
                      },
                    ]
                  : []),
                ...(tipAmountCents > 0
                  ? [
                      {
                        label: "Tip",
                        value: FormatCurrency(tipAmountCents / 100),
                      },
                    ]
                  : []),
                {
                  label: "Service Fee",
                  value: FormatCurrency(ticketFeesCents / 100),
                },
              ]}
            />
          ) : null}
        </>
      ) : null}
    </>
  );
});
